import React, { useMemo, useCallback } from 'react';
import {
  Button,
  Card,
  Checkbox,
  DatePicker,
  FileUpload,
  Icon,
  Input,
  Select,
  Textarea,
  Workflow,
} from '@repo/ui';
import type { MultiValue } from 'react-select';

import type { SelectOption } from '@repo/ui';
import { numberUtil } from '@xtnd/web/shared';
import { AppealType, EDIT_INELIGIBLE_MESSAGE } from '@repo/types';

import { type Timekeeper, type MatterInvoiceSummaryFile } from '../../../types';
import { AppealSummary } from '../../appeals/AppealSummary';
import { ExistingBARFilesSection } from '../FileAttachments';
import { AR_ATTACHMENT_ACCEPT, AR_ATTACHMENT_MAX_SIZE } from '../../../constants/attachments';
import { EditReconcileWarningBanner } from '../../../utils/editReconcileWarning';

interface AppealFormProps {
  appealType: AppealType;
  matterDisplayNumber: string;
  clientName: string;
  invoiceDate: string;
  isPartiallyPaid: boolean;
  invoiceNumber: string;
  outstandingBalance: number;
  amountPaid: number;
  totalAmount: number;
  creditsIssued: number;
  responsibleAttorneyName: string;
  timekeepers?: Timekeeper[];
  formData: AppealFormData;
  errors: Partial<Record<keyof AppealFormData, string>>;
  isLoading: boolean;
  actualBalance: number;
  onInputChange: (field: keyof AppealFormData, value: string) => void;
  onNumberChange: (field: keyof AppealFormData, value: string) => void;
  onDateChange: (field: keyof AppealFormData, date: Date | null) => void;
  onSelectChange: (field: keyof AppealFormData, value: MultiValue<SelectOption> | null) => void;
  onFilesChange: (files: File[]) => void;
  onBack: () => void;
  onSubmit: () => void;
  setError: (field: keyof AppealFormData, error: string) => void;
  existingFiles?: MatterInvoiceSummaryFile[];
  selectedExistingFiles?: MatterInvoiceSummaryFile[];
  onExistingFilesChange?: (files: MatterInvoiceSummaryFile[]) => void;
  isFetchingExistingFiles?: boolean;
  isEditMode?: boolean;
  /** Reconcile-warning lines from the parent; rendered above the submit action in edit mode. */
  editWarningLines?: React.ReactNode[];
  /** Edit trust gate: when true, the form renders read-only with the ineligible banner. */
  editBlocked?: boolean;
}

export interface AppealFormData {
  promptPayment: string;
  bulkDiscount: string;
  administrativeFee: string;
  feesCut: string;
  expensesCut: string;
  comment: string;
  deadline: string | null;
  attyDeadline: string | null;
  timekeepers: SelectOption[] | null;
  files: File[];
  appealedFees?: string;
  appealedExpenses?: string;
}

export interface TimekeeperOption extends SelectOption {
  id: string;
}

export const TIMEKEEPER_COLORS = [
  { color: '#3b82f5', bgColor: '#3b82f5' },
  { color: '#374151', bgColor: '#374151' },
  { color: '#1E40AF', bgColor: '#1E40AF' },
  { color: '#5B21B6', bgColor: '#5B21B6' },
  { color: '#065F46', bgColor: '#065F46' },
  { color: '#991B1B', bgColor: '#991B1B' },
  { color: '#4B5563', bgColor: '#4B5563' },
  { color: '#B45309', bgColor: '#B45309' },
  { color: '#0891B2', bgColor: '#0891B2' },
  { color: '#7C3AED', bgColor: '#7C3AED' },
];

const timekeeperOptions = (timekeepers?: Timekeeper[]): TimekeeperOption[] =>
  timekeepers?.map((t, index) => {
    const colorConfig = TIMEKEEPER_COLORS[index % TIMEKEEPER_COLORS.length]!;
    return {
      value: t.email,
      label: `${t.name}${t.initials ? ` (${t.initials})` : ''}${!t.isActive ? ` (Inactive)` : ''}`,
      id: t.id,
      color: colorConfig.color,
      bgColor: colorConfig.bgColor,
    };
  }) ?? [];

/**
 * Form component for appeal data entry
 * Displays different fields based on appeal type (backlog vs subsequent)
 */
export const AppealForm: React.FC<AppealFormProps> = ({
  appealType,
  isPartiallyPaid,
  invoiceNumber,
  outstandingBalance,
  amountPaid,
  matterDisplayNumber,
  clientName,
  invoiceDate,
  totalAmount,
  creditsIssued,
  responsibleAttorneyName,
  timekeepers,
  formData,
  errors,
  isLoading,
  actualBalance,
  onInputChange,
  onNumberChange,
  onDateChange,
  onSelectChange,
  onFilesChange,
  onBack,
  onSubmit,
  setError,
  existingFiles = [],
  selectedExistingFiles = [],
  onExistingFilesChange,
  isEditMode = false,
  editWarningLines = [],
  editBlocked = false,
}) => {
  // When the edit is trust-gated, every input is read-only (the BE 422s anyway).
  const inputsDisabled = isLoading || editBlocked;

  const isFormValid = useMemo(() => {
    const hasError = Object.values(errors ?? {}).some(error => Boolean(error));
    if (hasError) {
      return false;
    }

    // Require at least one attachment (new or existing) — but not in edit mode.
    if (!isEditMode) {
      const hasFiles = formData.files.length > 0 || (selectedExistingFiles?.length ?? 0) > 0;
      if (!hasFiles) {
        return false;
      }
    }

    return true;
  }, [errors, formData.files.length, selectedExistingFiles?.length, isEditMode]);

  const clientRefusedToPay = useMemo(() => {
    const feesCut = Number(formData.feesCut || 0);
    const expensesCut = Number(formData.expensesCut || 0);
    let clientRefusedToPay = feesCut + expensesCut;
    if (clientRefusedToPay < 0 || clientRefusedToPay > actualBalance) {
      clientRefusedToPay = 0;
    }
    return clientRefusedToPay;
  }, [formData.feesCut, formData.expensesCut, actualBalance]);

  const otherAdjustments = useMemo(() => {
    const promptPayment = Number(formData.promptPayment || 0);
    const bulkDiscount = Number(formData.bulkDiscount || 0);
    const adminFee = Number(formData.administrativeFee || 0);
    return promptPayment + bulkDiscount + adminFee;
  }, [formData.promptPayment, formData.bulkDiscount, formData.administrativeFee]);

  const agreedToPay = useMemo(() => {
    let agreedToPay = actualBalance - clientRefusedToPay - otherAdjustments;
    if (agreedToPay < 0) {
      agreedToPay = 0;
    }

    return agreedToPay;
  }, [clientRefusedToPay, actualBalance, otherAdjustments]);

  const handleExistingFileSelection = useCallback(
    (files: MatterInvoiceSummaryFile[]) => {
      if (!onExistingFilesChange) return;
      onExistingFilesChange(files);
    },
    [onExistingFilesChange]
  );

  const amountToBeAppealed = useMemo(() => {
    const appealedFees = Number(formData.appealedFees || 0);
    const appealedExpenses = Number(formData.appealedExpenses || 0);
    return appealedFees + appealedExpenses;
  }, [formData.appealedFees, formData.appealedExpenses]);

  const renderFinancialInfoContent = (appealType: AppealType) => {
    if (appealType === AppealType.BACKLOG) {
      return (
        <div>
          <Card className="overflow-hidden rounded-2xl border  shadow-lg !p-0 mb-4">
            <div className="flex items-center justify-between gap-3 bg-xtnd-light dark:bg-xtnd-blue/60 py-3 px-4">
              <div className="flex items-center gap-2">
                <Icon name="HiPencil" className="h-4 w-4 " />
                <h4 className="text-xs font-bold uppercase tracking-wider ">Client Refused From Original Invoice</h4>
              </div>
            </div>

            <div className="bg-white dark:bg-xtnd-dark-900 px-5 py-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center justify-between">
                    <label
                      htmlFor="feesCut"
                      className="block text-xs font-semibold text-xtnd-dark dark:text-xtnd-light mb-1"
                    >
                      Fees Cut <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <Input
                    id="feesCut"
                    type="number"
                    value={formData.feesCut}
                    onChange={e => onNumberChange('feesCut', e.target.value)}
                    error={errors.feesCut}
                    disabled={inputsDisabled}
                    required
                    fullWidth
                    placeholder="0.00"
                    className="mt-1 dark:border-xtnd-dark-600 bg-xtnd-white dark:bg-xtnd-dark-800"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <label
                      htmlFor="expensesCut"
                      className="block text-xs font-semibold text-xtnd-dark dark:text-xtnd-light mb-1"
                    >
                      Expenses Cut <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <Input
                    id="expensesCut"
                    type="number"
                    value={formData.expensesCut}
                    onChange={e => onNumberChange('expensesCut', e.target.value)}
                    error={errors.expensesCut}
                    disabled={inputsDisabled}
                    required
                    fullWidth
                    placeholder="0.00"
                    className="mt-1 dark:border-xtnd-dark-600 bg-xtnd-white dark:bg-xtnd-dark-800"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-4 border-t border-xtnd-light/60 dark:border-xtnd-blue/60  bg-xtnd-light/60 dark:bg-xtnd-blue/60 px-5 py-3 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-1 w-3/5">
                <div className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-full">
                  <Icon
                    name="HiInformationCircle"
                    className="h-4 w-4 text-xtnd-blue dark:text-xtnd-white"
                  />
                </div>
                <p className="text-xs text-xtnd-dark-600 dark:text-xtnd-white">
                  The amount the client refused to pay.
                </p>
              </div>

              <div className="text-right text-xtnd-blue dark:text-xtnd-white">
                <p className="text-[10px] font-bold uppercase tracking-widest mb-1 flex items-center">
                  Client Refused to Pay
                </p>
                <p className="mt-1 text-xl font-mono font-bold ">
                  {numberUtil.formatCurrency(clientRefusedToPay)}
                </p>
              </div>
            </div>
          </Card>
          <Card className="overflow-hidden rounded-2xl border  shadow-lg !p-0">
            <div className="flex items-center justify-between gap-3 bg-xtnd-light dark:bg-xtnd-blue/60 py-3 px-4">
              <div className="flex items-center gap-2">
                <Icon name="HiPencil" className="h-4 w-4 " />
                <h4 className="text-xs font-bold uppercase tracking-wider ">Prior Appeal Information </h4>
              </div>
            </div>

            <div className="bg-white dark:bg-xtnd-dark-900 px-5 py-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center justify-between">
                    <label
                      htmlFor="appealed-fees"
                      className="block text-xs font-semibold text-xtnd-dark dark:text-xtnd-light mb-1"
                    >
                      Appealed Fees <span className="text-red-500">*</span>
                    </label>
                    <p className="text-xs italic text-xtnd-dark-600 dark:text-xtnd-light-400">
                      Max: {numberUtil.formatCurrency(Number(formData.feesCut) || 0)}
                    </p>
                  </div>
                  <Input
                    id="appealed-fees"
                    type="number"
                    value={formData.appealedFees}
                    onChange={e => onNumberChange('appealedFees', e.target.value)}
                    error={errors.appealedFees}
                    disabled={inputsDisabled}
                    required
                    fullWidth
                    placeholder="0.00"
                    className="mt-1 dark:border-xtnd-dark-600 bg-xtnd-white dark:bg-xtnd-dark-800"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <label
                      htmlFor="appealed-expenses"
                      className="block text-xs font-semibold text-xtnd-dark dark:text-xtnd-light mb-1"
                    >
                      Appealed Expenses <span className="text-red-500">*</span>
                    </label>
                    <p className="text-xs italic text-xtnd-dark-600 dark:text-xtnd-light-400">
                      Max: {numberUtil.formatCurrency(Number(formData.expensesCut) || 0)}
                    </p>
                  </div>
                  <Input
                    id="appealed-expenses"
                    type="number"
                    value={formData.appealedExpenses}
                    onChange={e => onNumberChange('appealedExpenses', e.target.value)}
                    error={errors.appealedExpenses}
                    disabled={inputsDisabled}
                    required
                    fullWidth
                    placeholder="0.00"
                    className="mt-1 dark:border-xtnd-dark-600 bg-xtnd-white dark:bg-xtnd-dark-800"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-4 border-t border-xtnd-light/60 dark:border-xtnd-blue/60  bg-xtnd-light/60 dark:bg-xtnd-blue/60 px-5 py-3 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-1 w-3/5">
                <div className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-full">
                  <Icon
                    name="HiInformationCircle"
                    className="h-4 w-4 text-xtnd-blue dark:text-xtnd-white"
                  />
                </div>
                <p className="text-xs text-xtnd-dark-600 dark:text-xtnd-white">
                  The amount of a prior appeal to the client.
                </p>
              </div>

              <div className="text-right text-xtnd-blue dark:text-xtnd-white">
                <p className="text-[10px] font-bold uppercase tracking-widest mb-1 flex items-center">
                  Amount Appealed
                </p>
                <p className="mt-1 text-xl font-mono font-bold ">
                  {numberUtil.formatCurrency(amountToBeAppealed)}
                </p>
              </div>
            </div>
          </Card>

          <Card className="overflow-hidden rounded-2xl border  shadow-lg !p-0 mt-4">
            <div className="flex items-center justify-between gap-3 bg-xtnd-light dark:bg-xtnd-blue/60 py-3 px-4">
              <div className="flex items-center gap-2">
                <Icon name="HiPencil" className="h-4 w-4 " />
                <h4 className="text-xs font-bold uppercase tracking-wider ">Discounts</h4>
              </div>
            </div>

            <div className="bg-white dark:bg-xtnd-dark-900 px-5 py-5">
              <div className="">
                <div className="space-y-4 pt-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="promptPayment"
                        className="block text-xs font-medium text-xtnd-dark-600 dark:text-xtnd-light-400 mb-1"
                      >
                        Prompt Payment <span className="text-red-500">*</span>
                      </label>
                      <Input
                        id="promptPayment"
                        type="number"
                        value={formData.promptPayment}
                        onChange={e => onNumberChange('promptPayment', e.target.value)}
                        error={errors.promptPayment}
                        disabled={inputsDisabled}
                        fullWidth
                        placeholder="0.00"
                        className="dark:border-xtnd-dark-600"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="bulkDiscount"
                        className="block text-xs font-medium text-xtnd-dark-600 dark:text-xtnd-light-400 mb-1"
                      >
                        Bulk Discount <span className="text-red-500">*</span>
                      </label>
                      <Input
                        id="bulkDiscount"
                        type="number"
                        value={formData.bulkDiscount}
                        onChange={e => onNumberChange('bulkDiscount', e.target.value)}
                        error={errors.bulkDiscount}
                        disabled={inputsDisabled}
                        fullWidth
                        placeholder="0.00"
                        className="dark:border-xtnd-dark-600"
                      />
                    </div>
                    <div className="col-span-full">
                      <label
                        htmlFor="administrativeFee"
                        className="block text-xs font-medium text-xtnd-dark-600 dark:text-xtnd-light-400 mb-1"
                      >
                        Admin Fee <span className="text-red-500">*</span>
                      </label>
                      <Input
                        id="administrativeFee"
                        type="number"
                        value={formData.administrativeFee}
                        onChange={e => onNumberChange('administrativeFee', e.target.value)}
                        error={errors.administrativeFee}
                        disabled={inputsDisabled}
                        fullWidth
                        placeholder="0.00"
                        className="dark:border-xtnd-dark-600"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-4 border-t border-xtnd-light/60 dark:border-xtnd-blue/60  bg-xtnd-light/60 dark:bg-xtnd-blue/60 px-5 py-3 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-1 w-3/5">
                <div className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-full">
                  <Icon
                    name="HiInformationCircle"
                    className="h-4 w-4 text-xtnd-blue dark:text-xtnd-white"
                  />
                </div>
                <p className="text-xs text-xtnd-dark-600 dark:text-xtnd-white">
                  The amount of discounts applied to the invoice.
                </p>
              </div>

              <div className="text-right text-xtnd-blue dark:text-xtnd-white">
                <p className="text-[10px] font-bold uppercase tracking-widest mb-1 flex items-center">
                  Discounts
                </p>
                <p className="mt-1 text-xl font-mono font-bold ">
                  {numberUtil.formatCurrency(otherAdjustments)}
                </p>
              </div>
            </div>
          </Card>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Left Column: Input Fields */}
        <div className="flex flex-col gap-4">
          {/* Appeal Breakdown */}

          <h4 className="text-xs font-bold text-xtnd-dark-600 dark:text-xtnd-light-400 uppercase tracking-wider flex items-center">
            Client Refused
          </h4>
          <div>
            <label
              htmlFor="feesCut"
              className="block text-xs font-semibold text-xtnd-dark dark:text-xtnd-light mb-1"
            >
              Fees Cut <span className="text-red-500">*</span>
            </label>
            <Input
              id="feesCut"
              type="number"
              value={formData.feesCut}
              onChange={e => onNumberChange('feesCut', e.target.value)}
              error={errors.feesCut}
              disabled={inputsDisabled}
              required
              fullWidth
              placeholder="0.00"
              className="dark:border-xtnd-dark-600"
            />
          </div>
          <div>
            <label
              htmlFor="expensesCut"
              className="block text-xs font-semibold text-xtnd-dark dark:text-xtnd-light mb-1"
            >
              Expenses Cut <span className="text-red-500">*</span>
            </label>

            <Input
              id="expensesCut"
              type="number"
              value={formData.expensesCut}
              onChange={e => onNumberChange('expensesCut', e.target.value)}
              error={errors.expensesCut}
              disabled={inputsDisabled}
              required
              fullWidth
              placeholder="0.00"
              className=" dark:border-xtnd-dark-600"
            />
          </div>

          {/* Non-Appeal Adjustments */}
          <div className="space-y-4 pt-2">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-bold text-xtnd-dark-600 dark:text-xtnd-light-400 uppercase tracking-wider">
                Discounts
              </h4>
            </div>

            <div className="flex flex-col gap-4">
              <div>
                <label
                  htmlFor="promptPayment"
                  className="block text-xs font-medium text-xtnd-dark-600 dark:text-xtnd-light-400 mb-1"
                >
                  Prompt Payment <span className="text-red-500">*</span>
                </label>
                <Input
                  id="promptPayment"
                  type="number"
                  value={formData.promptPayment}
                  onChange={e => onNumberChange('promptPayment', e.target.value)}
                  error={errors.promptPayment}
                  disabled={inputsDisabled}
                  fullWidth
                  placeholder="0.00"
                  className="dark:border-xtnd-dark-600"
                />
              </div>
              <div>
                <label
                  htmlFor="bulkDiscount"
                  className="block text-xs font-medium text-xtnd-dark-600 dark:text-xtnd-light-400 mb-1"
                >
                  Bulk Discount <span className="text-red-500">*</span>
                </label>
                <Input
                  id="bulkDiscount"
                  type="number"
                  value={formData.bulkDiscount}
                  onChange={e => onNumberChange('bulkDiscount', e.target.value)}
                  error={errors.bulkDiscount}
                  disabled={inputsDisabled}
                  fullWidth
                  placeholder="0.00"
                  className="dark:border-xtnd-dark-600"
                />
              </div>
              <div className="col-span-full">
                <label
                  htmlFor="administrativeFee"
                  className="block text-xs font-medium text-xtnd-dark-600 dark:text-xtnd-light-400 mb-1"
                >
                  Admin Fee <span className="text-red-500">*</span>
                </label>
                <Input
                  id="administrativeFee"
                  type="number"
                  value={formData.administrativeFee}
                  onChange={e => onNumberChange('administrativeFee', e.target.value)}
                  error={errors.administrativeFee}
                  disabled={inputsDisabled}
                  fullWidth
                  placeholder="0.00"
                  className="dark:border-xtnd-dark-600"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Summary Cards */}
        <div className="flex flex-col justify-start space-y-6">
          {/*  */}

          <Card className=" rounded-xl p-5 shadow-sm ">
            <h4 className="text-xtnd-dark-600 dark:text-xtnd-light-400 text-[10px] font-bold uppercase tracking-widest mb-1 flex items-center">
              <Icon name="HiCurrencyDollar" className="w-3.5 h-3.5 mr-2" />
              Client Refused to Pay
            </h4>
            <p className="text-3xl font-mono font-bold text-xtnd-blue dark:text-xtnd-blue-300">
              {numberUtil.formatCurrency(clientRefusedToPay)}
            </p>
            <p className="mt-2 text-[11px] text-xtnd-dark-600 dark:text-xtnd-light-400 leading-relaxed italic">
              Client Refused to Pay is automatically calculated based on Fees Cut and Expenses Cut.
            </p>
          </Card>

          {/* Agreed to Pay Card */}
          <Card className=" rounded-xl p-5 shadow-sm ">
            <h4 className="text-xtnd-dark-600 dark:text-xtnd-light-400 text-[10px] font-bold uppercase tracking-widest mb-1 flex items-center">
              <Icon name="HiCreditCard" className="w-3.5 h-3.5 mr-2" />
              Agreed to Pay (Est.)
            </h4>
            <p className="text-3xl font-mono font-bold text-xtnd-blue dark:text-xtnd-blue-300">
              {numberUtil.formatCurrency(agreedToPay)}
            </p>
            <p className="mt-2 text-[11px] text-xtnd-dark-600 dark:text-xtnd-light-400 leading-relaxed italic">
              Remaining balance client is expected to pay after all reductions.
            </p>
          </Card>

          {/* Logic Summary Card */}
          <Card className="!bg-amber-50 dark:!bg-amber-900/20 border border-amber-100 dark:border-amber-800 rounded-xl p-5">
            <h4 className="text-amber-800 dark:text-amber-300 text-sm font-bold mb-2 flex items-center">
              <Icon name="HiInformationCircle" className="w-4 h-4 mr-2" />
              Logic Summary
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-amber-600 dark:text-amber-400">
                  Client Refused to Pay (Fees+Exp):
                </span>
                <span className="font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                  {numberUtil.formatCurrency(clientRefusedToPay)}
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-amber-600 dark:text-amber-400">Discounts:</span>
                <span className="font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                  {numberUtil.formatCurrency(otherAdjustments)}
                </span>
              </div>
              <div className=" flex justify-between text-xs border-t border-amber-200 dark:border-amber-400 pt-2 mt-2">
                <span className="text-amber-600 dark:text-amber-400">All Reductions</span>
                <span className="font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                  {numberUtil.formatCurrency(clientRefusedToPay + otherAdjustments)}
                </span>
              </div>
              <div className="pt-2 mt-2">
                <p className="text-[10px] text-amber-700 dark:text-amber-300 leading-relaxed">
                  Agreed to Pay = Outstanding ({numberUtil.formatCurrency(outstandingBalance)}) -
                  All Reductions ({numberUtil.formatCurrency(clientRefusedToPay + otherAdjustments)}
                  )
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col mt-4">

      <div className="flex flex-col lg:flex-row gap-6 pt-4">
        {/* Left Side: Form */}
        <div className="flex-1 space-y-6">
          {editBlocked && (
            <div
              role="alert"
              className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-4"
            >
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                {EDIT_INELIGIBLE_MESSAGE}
              </p>
            </div>
          )}
          {/* Back Button */}

          {/* Financial Information Section */}
          <Card className="p-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-xtnd-light dark:bg-xtnd-dark-700 rounded-lg flex items-center justify-center mr-4">
                  <Icon
                    name="HiCurrencyDollar"
                    className="w-5 h-5 text-xtnd-blue dark:text-xtnd-blue-300"
                  />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-xtnd-dark dark:text-xtnd-light">
                    Financial Information
                  </h2>
                  <p className="text-xs text-xtnd-dark-600 dark:text-xtnd-light-400 font-medium tracking-wide uppercase">
                    {appealType === AppealType.BACKLOG ? 'BACKLOG' : 'SUBSEQUENT'} MODE
                  </p>
                </div>
              </div>
            </div>
            {renderFinancialInfoContent(appealType)}
          </Card>

          {/* Attorney Workflow Section (Subsequent Only) */}
          {appealType === AppealType.SUBSEQUENT && (
            <Card
              className="p-6 opacity-0 animate-fade-in-up relative z-20"
              style={{ animationDelay: '0.2s' }}
            >
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 bg-amber-50 dark:bg-amber-900/20 rounded-lg flex items-center justify-center mr-4">
                  <Icon name="HiUsers" className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <h2 className="text-xl font-bold text-xtnd-dark dark:text-xtnd-light">
                  Attorney Workflow
                </h2>
              </div>
              <div className="flex flex-col gap-6">
                <div>
                  <label
                    htmlFor="attyDeadline"
                    className="block text-sm font-semibold text-xtnd-dark dark:text-xtnd-light mb-2"
                  >
                    Atty Response Deadline <span className="text-red-500">*</span>
                  </label>
                  <DatePicker
                    id="attyDeadline"
                    mode="single"
                    value={formData.attyDeadline ? new Date(formData.attyDeadline) : undefined}
                    onChange={date => onDateChange('attyDeadline', date)}
                    className="relative z-10"
                    buttonClassName="w-full justify-between"
                    required
                    disabled={inputsDisabled}
                    error={errors.attyDeadline}
                  />
                </div>
                <div>
                  <label
                    htmlFor="timekeepers"
                    className="block text-sm font-semibold text-xtnd-dark dark:text-xtnd-light mb-2"
                  >
                    Timekeepers <span className="text-red-500">*</span>
                  </label>
                  <Select
                    id="timekeepers"
                    options={timekeeperOptions(timekeepers)}
                    value={formData.timekeepers}
                    multiple
                    onChange={value => onSelectChange('timekeepers', value)}
                    error={errors.timekeepers}
                    disabled={inputsDisabled}
                    required
                    fullWidth
                  />
                </div>
              </div>
            </Card>
          )}

          {/* Submission Details Section */}
          <Card className="p-6 shadow-sm">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-gray-100 dark:bg-xtnd-dark-700 rounded-lg flex items-center justify-center mr-4">
                <Icon
                  name="HiDocumentText"
                  className="w-5 h-5 text-xtnd-dark-600 dark:text-xtnd-light-400"
                />
              </div>
              <h2 className="text-xl font-bold text-xtnd-dark dark:text-xtnd-light">
                Submission Details
              </h2>
            </div>

            {/* Client Appeal Deadline - Only for SUBSEQUENT */}
            {appealType === AppealType.SUBSEQUENT && (
              <div className="mb-4">
                <label
                  htmlFor="deadline"
                  className="block text-sm font-semibold text-xtnd-dark dark:text-xtnd-light mb-2"
                >
                  Client Appeal Deadline <span className="text-red-500">*</span>
                </label>
                <DatePicker
                  id="deadline"
                  mode="single"
                  value={formData.deadline ? new Date(formData.deadline) : undefined}
                  onChange={date => onDateChange('deadline', date)}
                  className="relative z-10"
                  buttonClassName="w-full justify-between"
                  required
                  disabled={inputsDisabled}
                  error={errors.deadline}
                />
              </div>
            )}

            <label
              htmlFor="comment"
              className="block text-sm font-semibold text-xtnd-dark dark:text-xtnd-light mb-2"
            >
              Comment <span className="text-red-500">*</span>
            </label>
            <Textarea
              id="comment"
              value={formData.comment}
              onChange={e => onInputChange('comment', e.target.value)}
              disabled={inputsDisabled}
              rows={4}
              fullWidth
              required
              error={errors.comment}
              placeholder="Explain the reason for this appeal or backlog entry..."
            />

            <div className="mt-4 space-y-4">
              <label
                htmlFor="files"
                className="flex flex-col text-sm font-semibold text-xtnd-dark dark:text-xtnd-light mb-1"
              >
                <p>
                  Attachments <span className="text-red-500">*</span>
                </p>
                <span className="text-xs text-gray-400 dark:text-gray-200 italic font-normal">
                  Please include the original BAR and the appeal BAR.
                </span>
              </label>
              <FileUpload
                id="files"
                files={formData.files}
                isMultiple={true}
                accept={AR_ATTACHMENT_ACCEPT}
                maxSize={AR_ATTACHMENT_MAX_SIZE}
                disabled={inputsDisabled}
                onChange={onFilesChange}
                isDisplayUploadButton={false}
                maxFileNumber={12}
                error={errors.files}
                setError={(error: string) => setError('files', error)}
              />

              {/* Existing BAR Files Selection */}
              {existingFiles && existingFiles.length > 0 && (
                <ExistingBARFilesSection
                  existingFiles={existingFiles}
                  onSelectedFilesChange={handleExistingFileSelection}
                  columns={1}
                />
              )}
            </div>
          </Card>
          {appealType === AppealType.SUBSEQUENT && (
            <Workflow
              label={
                <div className="flex items-center gap-2">
                  <Icon name="FiAlertTriangle" className="w-5 h-5 mr-2 text-yellow" />
                  Appeal workflow
                </div>
              }
              steps={[
                { index: 1, label: 'To Be Appealed' },
                { index: 2, label: 'Notify Timekeepers' },
                { index: 3, label: 'Timekeeper Response' },
              ]}
            />
          )}
        </div>

        {/* Right Side: Invoice Summary Sidebar */}
        <div className="lg:w-80 space-y-6">
          <AppealSummary
            basicInformation={{
              matterDisplayNumber,
              clientName,
              invoiceDate,
              invoiceNumber,
              totalAmount,
              amountDue: outstandingBalance,
              paid: amountPaid,
              creditsIssued,
              responsibleAttorneyName,
            }}
          />

          <Card
            className="!bg-transparent text-xtnd-dark dark:text-xtnd-light p-6 rounded-2xl shadow-xl sticky top-24 opacity-0 animate-fadeInRight"
            style={{ animationDelay: '0.15s' }}
          >
            <div className="space-y-4">
              <div className="flex justify-between items-center font-bold text-xtnd-dark dark:text-xtnd-light">
                <span className="text-xs">Client Refused to Pay</span>
                <span className=" font-mono">{numberUtil.formatCurrency(clientRefusedToPay)}</span>
              </div>

              <div className="p-3 bg-xtnd-blue/50 rounded-xl border border-xtnd-blue/80 shadow-sm">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-[0.625rem] shadow-sm text-xtnd-dark dark:text-xtnd-light uppercase font-bold">
                    Agreed to Pay
                  </span>
                  <span className="text-xtnd-dark dark:text-xtnd-light font-mono font-bold text-lg">
                    {numberUtil.formatCurrency(agreedToPay)}
                  </span>
                </div>
                <div className="w-full bg-xtnd-dark-700 h-1.5 rounded-full overflow-hidden my-2">
                  <div
                    className="bg-xtnd-blue dark:bg-xtnd-light h-full transition-all duration-500"
                    style={{
                      width:
                        actualBalance > 0
                          ? `${Math.min(100, Math.max(0, (agreedToPay / actualBalance) * 100))}%`
                          : '0%',
                    }}
                  />
                </div>
              </div>

              {appealType === AppealType.BACKLOG && (
                <div>
                  <div className="flex flex-col justify-start space-y-6 mb-4">
                    {/* Logic Summary Card */}
                    <Card className="!bg-amber-50 dark:!bg-amber-900/20 border border-amber-100 dark:border-amber-800 rounded-xl p-5">
                      <h4 className="text-amber-800 dark:text-amber-300 text-sm font-bold mb-2 flex items-center">
                        <Icon name="HiInformationCircle" className="w-4 h-4 mr-2" />
                        Logic Summary
                      </h4>
                      <div className="space-y-2">
                        <div className="flex justify-between text-xs">
                          <span className="text-amber-600 dark:text-amber-400">
                            Client Refused to Pay (Fees+Exp):
                          </span>
                          <span className="font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                            {numberUtil.formatCurrency(clientRefusedToPay)}
                          </span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-amber-600 dark:text-amber-400">Discounts:</span>
                          <span className="font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                            {numberUtil.formatCurrency(otherAdjustments)}
                          </span>
                        </div>
                        <div className=" flex justify-between text-xs border-t border-amber-200 dark:border-amber-400 pt-2 mt-2">
                          <span className="text-amber-600 dark:text-amber-400">All Reductions</span>
                          <span className="font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                            {numberUtil.formatCurrency(clientRefusedToPay + otherAdjustments)}
                          </span>
                        </div>
                        <div className="pt-2 mt-2">
                          <p className="text-[10px] text-amber-700 dark:text-amber-300 leading-relaxed">
                            Agreed to Pay = Outstanding (
                            {numberUtil.formatCurrency(outstandingBalance)}) - All Reductions (
                            {numberUtil.formatCurrency(clientRefusedToPay + otherAdjustments)})
                          </p>
                        </div>
                      </div>
                    </Card>
                  </div>
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
                    <div className="flex items-start gap-2">
                      <Icon
                        name="HiInformationCircle"
                        className="w-4 h-4 mt-0.5 text-blue-600 dark:text-blue-400 flex-shrink-0"
                      />
                      <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
                        <span className="font-medium">Backlog mode enabled.</span> Write-offs are
                        automatically approved and no attorney tasks will be created.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <EditReconcileWarningBanner lines={editWarningLines} />

            <Button
              variant="primary"
              onClick={onSubmit}
              disabled={!isFormValid || isLoading || editBlocked}
              loading={isLoading}
              fullWidth
              className="mt-6"
            >
              {isEditMode ? 'Save Changes' : appealType === AppealType.BACKLOG ? 'Sync Backlog' : 'Submit Appeal'}
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AppealForm;
